import { useEffect } from 'react';
import type { FC } from 'react';
import { Text } from '@chakra-ui/react';
import { useBasket } from '../../bff-api/baskets/useBasket';
import { isBasketEligibleForMonthlyLoan } from '../../utils/afterPay/isBasketEligibleForMonthlyLoan';
import { useCustomPreferences } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { AfterpayButton } from './afterpay-button/AfterpayButton';
import styles from './afterpay.module.scss';
import type { PaymentMethodConfig } from '../../bff-api/baskets/model';

const Afterpay: FC<{
    afterpayConfig: PaymentMethodConfig;
}> = ({ afterpayConfig }) => {
    const { data: basket } = useBasket();
    const preferences = useCustomPreferences([
        CustomPreference.AFTERPAY_MPID,
        CustomPreference.AFTERPAY_PLACEMENT_ID,
    ]);

    const mpid = preferences[CustomPreference.AFTERPAY_MPID];
    const placementId = preferences[CustomPreference.AFTERPAY_PLACEMENT_ID];

    useEffect(() => {
        const widgetsFound = document.getElementsByTagName('afterpay-placement');
        if (widgetsFound.length) {
            const afterpayWidget = widgetsFound[0] as Element & { afterpayPriceTable?: Element };

            if (afterpayWidget.afterpayPriceTable && afterpayWidget.afterpayPriceTable.shadowRoot) {
                const afterpayPriceTableSheet = new CSSStyleSheet();
                afterpayPriceTableSheet.replaceSync(afterpayPriceTableStyle);
                afterpayWidget.afterpayPriceTable.shadowRoot.adoptedStyleSheets = [
                    afterpayPriceTableSheet,
                ];
            }
        }
    }, []);

    const isAfterpayPayByMonthEnabled = afterpayConfig.payByMonthEnabled || false;
    const afterPayMinThreshold = afterpayConfig.minThreshold;
    const afterPayMaxThreshold = afterpayConfig.maxThreshold;

    if (!basket?.orderTotal || !mpid || !placementId) {
        return null;
    }

    const enableLoan = isBasketEligibleForMonthlyLoan(
        basket?.orderTotal,
        afterpayConfig.payByMonthEnabled,
    );

    return (
        <div
            id="afterpay-widget-container"
            data-testid="afterpay-widget-container"
            className={`${styles.afterpay} flex w-full flex-col justify-center p-4`}
        >
            {enableLoan ? (
                <square-placement
                    data-mpid={mpid}
                    data-placement-id={placementId}
                    data-amount={basket.orderTotal}
                    data-min={afterPayMinThreshold}
                    data-max={afterPayMaxThreshold}
                    data-testid="afterpay-messaging-widget"
                    data-locale="en_US"
                    data-currency="USD"
                    data-cart-is-eligible
                    data-intro-text="false"
                    data-apr-loans-available="true"
                />
            ) : (
                <>
                    <Text textStyle="slt-body-text-xl" fontWeight={700} textAlign="center">
                        How it works
                    </Text>

                    <square-placement
                        data-mpid={mpid}
                        data-placement-id={placementId}
                        data-amount={basket.orderTotal}
                        data-testid="afterpay-messaging-widget"
                        data-type="payment-schedule"
                        data-currency="USD"
                        data-locale="en_US"
                        data-apr-loans-available={isAfterpayPayByMonthEnabled ? 'true' : undefined}
                    />
                </>
            )}
            <Text textStyle="slt-body-text-lg" paddingTop={1} textAlign="center">
                You will be redirected to Afterpay to fill out your payment and then will return
                here to complete your order.
            </Text>
            <AfterpayButton afterpayConfig={afterpayConfig} />
        </div>
    );
};

const afterpayPriceTableStyle = `
    .first-harvey-ball,
    .second-harvey-ball,
    .third-harvey-ball,
    .fourth-harvey-ball {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
    .first-harvey-ball-text,
    .second-harvey-ball-text,
    .third-harvey-ball-text,
    .fourth-harvey-ball-text {
        margin-left: 0.5rem;
        text-align: start;
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 500;
        line-height: 1.25rem;
        letter-spacing: 0.01563rem;
    }
    .harvey-ball {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
    .harvey-ball, .harvey-ball-svg {
        width: 2.125rem;
        height: 2.125rem;
    }
    .harvey-balls-table {
        grid-template-columns: repeat(4,max-content);
        justify-content: center;
        gap: 2rem;
    }
`;

export { Afterpay };
